import { Navigate, useLocation, useParams } from 'react-router-dom'
import { HOME_ROUTE, STUDENT_ROUTE } from '../../app/router/routes'
import { StudentDetailLayout, StudentHistoryWidget, StudentWalletHistoryWidget, StudentWidget } from '../../widgets/student'
import { LayoutPages } from '../layoutPages'
import { useAppSelector } from '../../app/store/store'
import { Transactions } from '../../widgets/transactions'
import { StudentComments } from '../../widgets/studentComments'
import { FEATURES, FEATURE_SECTIONS, hasFeature, hasFeatureGrant } from '../../entities/my'
import idmClasses from '../../widgets/idm/ui/idm/idmWidget.module.scss'
import classes from './student.module.scss'
import { StudentLessonsCalendar } from './calendar/StudentLessonsCalendar'
import { StudentScheduleCalendar } from './calendar/StudentScheduleCalendar'
import { OpenContainer } from '../../features/openContainer'

export default function StudentPage() {

    const {id} = useParams<{id: string}>()
    const {pathname} = useLocation()
    const {my} = useAppSelector(s => s.myReducer)
    const canViewCalendars = hasFeature(my, FEATURE_SECTIONS.CALENDAR)
    const isHistoryRoute = pathname.endsWith('/history')
    const isWalletHistoryRoute = pathname.endsWith('/wallet-history')
    const targetStudentId = id ? Number(id) : 0
    const canReadScheduleHistory = Boolean(targetStudentId) && hasFeature(my, FEATURES.AUDIT.SCHEDULE_HISTORY_READ, targetStudentId)
    const canReadWalletHistory = canReadScheduleHistory

    const canOpenStudentCard =
        hasFeature(my, FEATURE_SECTIONS.STUDENTS)
        && hasFeatureGrant(my, FEATURES.STUDENTS.CARD_READ)
        && (
            hasFeatureGrant(my, FEATURES.STUDENTS.VIEW_FULL)
            || hasFeatureGrant(my, FEATURES.STUDENTS.VIEW_TUTOR)
        )

    if(!id || !canOpenStudentCard){
        return <Navigate to={HOME_ROUTE.path} />
    }

    if(isHistoryRoute && !canReadScheduleHistory){
        return <Navigate to={STUDENT_ROUTE.path.replace(':id', String(targetStudentId))} />
    }

    if(isWalletHistoryRoute && !canReadWalletHistory){
        return <Navigate to={STUDENT_ROUTE.path.replace(':id', String(targetStudentId))} />
    }

    return (
        <LayoutPages title={STUDENT_ROUTE.name}>
            {isWalletHistoryRoute
                ? <StudentWalletHistoryWidget key={pathname} />
                : isHistoryRoute
                ? <StudentHistoryWidget key={pathname} />
                : <StudentDetailLayout
                    studentId={+id}
                    selectedTab={1}
                    showHistoryTab={canReadScheduleHistory}
                    showWalletHistoryTab={canReadWalletHistory}
                >
                    <StudentWidget
                        id={+id}
                        afterLessons={student => canViewCalendars ? (
                            <section className={classes.sections}>
                                <section className={`${idmClasses.panel} ${classes.sectionItem}`}>
                                    <OpenContainer
                                        title="Календарь занятий"
                                        allowOverflow
                                        icon={<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="4" y="5" width="16" height="15" rx="2" /><path d="M8 3V7M16 3V7M4 10H20M8 14H10M14 14H16" /></svg>}
                                    >
                                        <section className={classes.openContent}>
                                            <StudentLessonsCalendar
                                                student={student}
                                                tutorUserId={my.role === 'tutor' ? my.id : undefined}
                                                tutorFullName={my.role === 'tutor' ? my.full_name : undefined}
                                                hideTitle
                                            />
                                        </section>
                                    </OpenContainer>
                                </section>
                                {
                                    my.role !== 'tutor' && !student.is_archive
                                        &&
                                    <section className={`${idmClasses.panel} ${classes.sectionItem}`}>
                                        <OpenContainer
                                            title="Регулярный график"
                                            allowOverflow
                                            icon={<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="4" y="5" width="16" height="15" rx="2" /><path d="M8 3V7M16 3V7M4 10H20M8 14L10 16L15 11" /></svg>}
                                        >
                                            <section className={classes.openContent}>
                                                <StudentScheduleCalendar student={student} hideTitle />
                                            </section>
                                        </OpenContainer>
                                    </section>
                                }
                            </section>
                        ) : null}
                        afterContent={student => {
                            const targetUserId = Number(student.user_id ?? 0)
                            const canViewTransactions = hasFeature(my, FEATURES.STUDENTS.TRANSACTIONS_LIST, targetUserId)
                            const canViewComments = hasFeature(my, FEATURES.STUDENTS.CARD_COMMENTS_READ, targetUserId)

                            return (
                                <section className={classes.sections}>
                                    {canViewTransactions &&
                                        <section className={`${idmClasses.panel} ${classes.sectionItem}`}>
                                            <Transactions studentId={student.id} targetUserId={targetUserId} />
                                        </section>
                                    }
                                    {canViewComments &&
                                        <section className={`${idmClasses.panel} ${classes.sectionItem}`}>
                                            <StudentComments studentId={student.id} targetUserId={targetUserId} />
                                        </section>
                                    }
                                </section>
                            )
                        }}
                    />
                </StudentDetailLayout>
            }
        </LayoutPages>
    )
}
